// HSSNA 全局错误处理中心 — 统一拦截网络异常并触发 UI 反馈
// 规格: SPEC §4.4.4 错误动作路由 / §7.1 异常状态 / H-ENG-13
//
// 职责:
//   - 接收 HssnaError / 超时 / 断网 / 500 等异常
//   - 根据 handleError(error) 动作路由, 统一触发全局 Toast / Snackbar / Modal / 跳转
//   - 避免每个业务组件单独 try-catch + toast (DRY)
//
// 接入方式:
//   1. 应用入口注入 Toast/Modal/Sentry 渲染器: setupGlobalErrorHandler({ showToast, ... })
//   2. TanStack Query onError / 拦截器 catch / 业务 catch 统一调用 dispatchGlobalError(error)
//   3. 也可包裹 Promise: withGlobalError(promise) 自动 catch + dispatch

import { handleError, isHssnaError, parseProblemDetails } from './handler.js';
import type { HssnaError } from './types.js';
import { ErrorCode } from './codes-gen.js';

/** Toast 级别 (与 ui-kit Toast 组件对齐) */
export type ToastLevel = 'info' | 'success' | 'warning' | 'error';

/** Toast 渲染器 (由各 app 注入, 如基于 ui-kit Toast 或 sonner) */
export interface ToastRenderer {
  (message: string, level: ToastLevel): void;
}

/** Modal 渲染器 (用于 IDOR 越权等需用户确认的严重错误) */
export interface ModalRenderer {
  (error: HssnaError): void;
}

/** 登录跳转器 (401 refresh 失败后, 跳转登录页) */
export interface LoginRedirector {
  (redirectBack?: string): void;
}

/** 全局错误处理器配置 (应用入口注入) */
export interface GlobalErrorHandlerOptions {
  /** Toast 渲染器 (必填, show-toast 动作依赖此) */
  readonly showToast?: ToastRenderer;
  /** Modal 渲染器 (show-modal 动作, 如 IDOR 越权弹窗) */
  readonly showModal?: ModalRenderer;
  /** 登录跳转器 (redirect-login 动作) */
  readonly redirectLogin?: LoginRedirector;
  /** 自定义重试器 (retry 动作, 默认仅记录由 TanStack Query 处理) */
  readonly onRetry?: (error: HssnaError) => void;
  /** 静默错误码集合 (这些错误码不触发 Toast, 如 AI 低置信度已转人工) */
  readonly silentCodes?: readonly string[];
}

// ===== 单例配置 =====
let _toast: ToastRenderer | undefined;
let _modal: ModalRenderer | undefined;
let _redirectLogin: LoginRedirector | undefined;
let _onRetry: ((error: HssnaError) => void) | undefined;
let _silentCodes: ReadonlySet<string> = new Set();
let _listenersInstalled = false;

/** 中文错误提示映射 (覆盖常见 HssnaError 的技术性 title, 转用户友好文案) */
const FRIENDLY_MESSAGES: Record<string, string> = {
  [ErrorCode.HSSNA_401_001]: '登录已过期,请重新登录',
  [ErrorCode.HSSNA_403_001]: '您没有权限执行此操作',
  [ErrorCode.HSSNA_403_002]: '检测到越权访问,已记录并上报',
  [ErrorCode.HSSNA_404_001]: '请求的资源不存在',
  [ErrorCode.HSSNA_409_003]: '该操作已提交,请勿重复点击',
  [ErrorCode.HSSNA_429_001]: '操作过于频繁,请稍后再试',
  [ErrorCode.HSSNA_500_001]: '服务器开小差了,请稍后重试',
  [ErrorCode.HSSNA_503_001]: '系统维护中,请稍后重试',
};

/**
 * 应用入口注入全局错误处理器 (仅调用一次)
 *
 * @example
 * ```ts
 * // apps/xxx/main.tsx
 * import { setupGlobalErrorHandler } from '@hssna/ts-shared';
 * import { toast } from 'sonner';
 *
 * setupGlobalErrorHandler({
 *   showToast: (msg, level) => toast[level](msg),
 *   showModal: (err) => setIrorModalState(err),
 *   redirectLogin: (back) => navigate(`/login?redirect=${back ?? ''}`),
 * });
 * ```
 */
export function setupGlobalErrorHandler(options: GlobalErrorHandlerOptions): void {
  _toast = options.showToast;
  _modal = options.showModal;
  _redirectLogin = options.redirectLogin;
  _onRetry = options.onRetry;
  if (options.silentCodes) {
    _silentCodes = new Set(options.silentCodes);
  }
  // P2-12: 自动注册 window.onerror / unhandledrejection 全局监听
  installGlobalErrorListeners();
}

/**
 * 注册全局未捕获错误监听 (P2-12)
 *
 * - window 'error' 事件: 捕获未 try-catch 的同步运行时错误 (语法错误/空引用等)
 * - window 'unhandledrejection' 事件: 捕获未 .catch() 的 Promise rejection
 *
 * 两者均派发到 dispatchGlobalError, 经 normalizeError 标准化后触发 Toast/Sentry.
 * 幂等: 多次调用仅注册一次 (HMR/测试安全).
 */
export function installGlobalErrorListeners(): void {
  if (_listenersInstalled) return;
  if (typeof window === 'undefined') return; // SSR 安全

  // 1. 未捕获同步错误 (script error / runtime error)
  window.addEventListener('error', (event: ErrorEvent) => {
    // 跨域脚本的 error 事件 message 为 'Script error.', 无法获取详情
    const error = event.error ?? event.message;
    void dispatchGlobalError(error);
    // 上报 Sentry (如已注入)
    reportToSentry(event.error ?? event.message);
  });

  // 2. 未捕获 Promise rejection
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    void dispatchGlobalError(reason);
    reportToSentry(reason);
  });

  _listenersInstalled = true;
}

/**
 * 上报到全局 Sentry (如已注入 @sentry/* SDK)
 * 与 interceptors.ts defaultReportIncident 保持一致的上报方式
 */
function reportToSentry(error: unknown): void {
  const globalSentry = (
    globalThis as { Sentry?: { captureException: (e: unknown) => void } }
  ).Sentry;
  if (globalSentry && typeof globalSentry.captureException === 'function') {
    globalSentry.captureException(error);
  }
}

/**
 * 统一派发错误到全局处理器
 *
 * 根据 handleError(error) 返回的动作列表逐一执行:
 *   - show-toast    → 调用注入的 ToastRenderer (除非错误码在 silentCodes 中)
 *   - show-modal    → 调用注入的 ModalRenderer
 *   - redirect-login → 调用注入的 LoginRedirector
 *   - retry         → 调用注入的 onRetry (默认无操作, 由 TanStack Query retry 处理)
 *   - report-idor   → 仅记录 (实际上报在拦截器层完成)
 *   - silent        → 无操作
 *
 * @param error HssnaError 或 unknown (自动尝试解析)
 */
export async function dispatchGlobalError(error: unknown): Promise<void> {
  const hssnaError = await normalizeError(error);
  if (!hssnaError) return;

  // 静默错误码直接跳过
  if (_silentCodes.has(hssnaError.code)) return;

  const actions = handleError(hssnaError);

  for (const action of actions) {
    switch (action) {
      case 'show-toast': {
        const message = FRIENDLY_MESSAGES[hssnaError.code] ?? hssnaError.title;
        _toast?.(message, 'error');
        break;
      }
      case 'show-modal':
        _modal?.(hssnaError);
        break;
      case 'redirect-login':
        _redirectLogin?.(typeof window !== 'undefined' ? window.location.pathname : undefined);
        break;
      case 'retry':
        _onRetry?.(hssnaError);
        break;
      case 'report-idor':
        // 实际上报在拦截器层完成 (defaultReportIncident), 此处仅记录
        // eslint-disable-next-line no-console
        console.error('[HSSNA] IDOR 安全事件:', hssnaError.code, hssnaError.detail);
        break;
      case 'silent':
        // 无操作
        break;
    }
  }
}

/**
 * 包裹 Promise, 自动 catch 并派发全局错误
 *
 * @example
 * ```ts
 * const data = await withGlobalError(apiFetch('/v1/alerts').then(r => unwrap(r)));
 * // 失败时自动弹 Toast, 不需要业务层 try-catch
 * ```
 */
export function withGlobalError<T>(promise: Promise<T>): Promise<T> {
  return promise.catch((err) => {
    dispatchGlobalError(err);
    throw err; // 重新抛出, 让调用方可选地处理 (如重置表单)
  });
}

/**
 * 将 unknown 错误标准化为 HssnaError
 * - 已是 HssnaError → 直接返回
 * - 是 Response → parseProblemDetails
 * - 是 TypeError (网络错误/断网) → 构造网络错误
 * - 是 AbortError (超时) → 构造超时错误
 * - 其他 → 构造 500 兜底错误
 */
async function normalizeError(error: unknown): Promise<HssnaError | null> {
  if (!error) return null;

  if (isHssnaError(error)) return error;

  // fetch Response (调用方直接传入响应)
  if (error instanceof Response) {
    return await parseProblemDetails(error);
  }

  // TypeError: 网络错误 / 断网 / CORS
  if (error instanceof TypeError) {
    return {
      code: ErrorCode.HSSNA_503_001,
      title: '网络连接异常',
      status: 503,
      detail: error.message || '无法连接到服务器,请检查网络后重试',
    };
  }

  // AbortError: 请求超时 (fetch signal abort)
  if (error instanceof DOMException && error.name === 'AbortError') {
    return {
      code: ErrorCode.HSSNA_504_001,
      title: '请求超时',
      status: 504,
      detail: '请求超时,请检查网络后重试',
    };
  }

  // 兜底: 未知错误
  const message = error instanceof Error ? error.message : String(error);
  return {
    code: ErrorCode.HSSNA_500_001,
    title: '发生未知错误',
    status: 500,
    detail: message,
  };
}
